/**
 * EditContactModal — add or edit an account contact's details (name, email,
 * phone, role) in a dialog. Opened from the pencil / "Add contact" button on
 * the AccountSidebar contact card. Pass `contact: null` for add mode.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {trpc} from '@common-web/lib/trpc';

export interface EditableContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  isPrimary: boolean;
}

interface EditContactModalProps {
  accountsId: string;
  /** Contact to edit, or null to add a new one. */
  contact: EditableContact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditContactModal({
  accountsId,
  contact,
  open,
  onOpenChange,
}: EditContactModalProps): React.ReactElement {
  const isAdding = contact === null;
  const utils = trpc.useUtils();

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [role, setRole] = React.useState('');

  // Seed the form each time the modal opens — from the contact when editing,
  // blank when adding.
  React.useEffect(() => {
    if (!open) return;
    setFirstName(contact?.firstName ?? '');
    setLastName(contact?.lastName ?? '');
    setEmail(contact?.email ?? '');
    setPhone(contact?.phone ?? '');
    setRole(contact?.role ?? '');
  }, [open, contact]);

  const onSaved = {
    onSuccess: () => {
      utils.account.getById.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
      onOpenChange(false);
    },
  };
  const update = trpc.account.updateContact.useMutation(onSaved);
  const add = trpc.account.addContact.useMutation(onSaved);
  const isPending = update.isPending || add.isPending;
  const error = update.error ?? add.error;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (contact) {
      update.mutate({
        contactId: contact.id,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        role: role.trim() || null,
      });
    } else {
      // Adding only happens from the empty Primary Contact slot — the new
      // contact is the primary (the server demotes any stray existing one).
      add.mutate({
        accountsId,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        role: role.trim() || undefined,
        isPrimary: true,
      });
    }
  }

  function handleClose(): void {
    if (isPending) return;
    update.reset();
    add.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className='sm:max-w-md'>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isAdding ? 'Add contact' : 'Edit contact'}</DialogTitle>
            <DialogDescription>
              {isAdding
                ? 'Add a contact to this account.'
                : 'Update the contact’s details for this account.'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-1.5'>
                <Label htmlFor='contact-first-name'>First name</Label>
                <Input
                  id='contact-first-name'
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='contact-last-name'>Last name</Label>
                <Input
                  id='contact-last-name'
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='contact-email'>Email</Label>
              <Input
                id='contact-email'
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-1.5'>
                <Label htmlFor='contact-phone'>Phone</Label>
                <Input
                  id='contact-phone'
                  type='tel'
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='contact-role'>Role</Label>
                <Input
                  id='contact-role'
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder='e.g. HR Manager'
                />
              </div>
            </div>

            {error ? <p className='text-sm text-red-600'>{error.message}</p> : null}
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type='submit' disabled={isPending}>
              {isPending ? 'Saving…' : isAdding ? 'Add contact' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
